import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { deleteUser } from 'firebase/auth';
import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { useGo } from '../components/Nav';
import { Banner, Button, ErrorText, Input, Loading, PageHeader } from '../components/ui';
import { ApiError, errorMessage } from '../lib/api';
import { auth } from '../lib/firebase';
import { deleteAccount, fetchMe, removeAvatar, updateMe, uploadAvatar } from '../queries/account';

/**
 * Your account, the parts of it that are yours to change: name, picture, and
 * the account itself. Email belongs to the sign-in provider.
 *
 * Also the page the Mac app's "Edit account" opens.
 */
export function AccountPage() {
  const { user, signOut } = useAuth();
  const queryClient = useQueryClient();
  const go = useGo();
  const me = useQuery({ queryKey: ['me'], queryFn: fetchMe });

  const [name, setName] = useState('');
  const [confirmDelete, setConfirmDelete] = useState('');
  const fileInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (me.data) setName(me.data.name ?? '');
  }, [me.data]);

  const settle = (next: Awaited<ReturnType<typeof fetchMe>>) => queryClient.setQueryData(['me'], next);

  const rename = useMutation({
    mutationFn: () => updateMe({ name: name.trim() || null }),
    onSuccess: settle,
  });
  const picture = useMutation({ mutationFn: uploadAvatar, onSuccess: settle });
  const clearPicture = useMutation({ mutationFn: removeAvatar, onSuccess: settle });
  const remove = useMutation({
    mutationFn: async () => {
      await deleteAccount();
      // The API row is gone; now the identity. Firebase insists on a recent
      // sign-in for this, and if it refuses, the person is signed out and
      // told to sign in and finish; the next API call would recreate them
      // otherwise.
      const current = auth.currentUser;
      if (current) {
        try {
          await deleteUser(current);
        } catch (cause) {
          await signOut();
          const code = (cause as { code?: string })?.code;
          throw new Error(
            code === 'auth/requires-recent-login'
              ? 'Your Slate data is deleted. Sign in once more and delete your account again to remove the sign-in itself.'
              : errorMessage(cause),
          );
        }
      }
    },
    onSuccess: () => go('/login', { replace: true }),
  });

  if (me.isPending) return <Loading label="Loading your account" />;
  if (me.isError) {
    return (
      <>
        <PageHeader title="Account" />
        <Banner tone="danger" title="Could not load your account">
          {errorMessage(me.error)}
        </Banner>
      </>
    );
  }

  const initial = (me.data.name || me.data.email || '?').trim().charAt(0).toUpperCase();
  const dirty = (me.data.name ?? '') !== name.trim() && !(me.data.name === null && name.trim() === '');
  const canDelete = confirmDelete.trim().toLowerCase() === 'delete';

  return (
    <>
      <PageHeader title="Account" subtitle="Your name and picture are what people see on your recordings." />

      <div className="space-y-6">
        <section className="card p-4 sm:p-5">
          <h2 className="text-h4 text-chalk">Profile picture</h2>
          <div className="mt-4 flex items-center gap-4">
            {me.data.avatarUrl ? (
              <img src={me.data.avatarUrl} alt="" referrerPolicy="no-referrer" className="h-16 w-16 rounded-full object-cover" />
            ) : (
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-slate-800 text-h3 text-chalk">
                {initial}
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              <input
                ref={fileInput}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) picture.mutate(file);
                  event.target.value = '';
                }}
              />
              <Button size="sm" busy={picture.isPending} onClick={() => fileInput.current?.click()}>
                {me.data.avatarUrl ? 'Change picture' : 'Upload a picture'}
              </Button>
              {me.data.avatarUrl ? (
                <Button size="sm" busy={clearPicture.isPending} onClick={() => clearPicture.mutate()}>
                  Remove
                </Button>
              ) : null}
            </div>
          </div>
          <p className="mt-3 text-caption text-chalk-faint">PNG, JPEG or WebP, up to 2 MB. Square works best.</p>
          {picture.isError ? (
            <div className="mt-2">
              <ErrorText>{errorMessage(picture.error)}</ErrorText>
            </div>
          ) : null}
        </section>

        <section className="card p-4 sm:p-5">
          <h2 className="text-h4 text-chalk">Name and contact</h2>
          <form
            className="mt-4 space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              rename.mutate();
            }}
          >
            <Input label="Name" value={name} maxLength={80} onChange={(event) => setName(event.target.value)} />
            <Input label="Email" value={me.data.email} readOnly disabled />
            <p className="text-caption text-chalk-faint">
              Email comes from how you sign in ({user?.providerData[0]?.providerId === 'google.com' ? 'Google' : 'email and password'}) and changes there.
            </p>
            <div className="flex items-center gap-3">
              <Button type="submit" variant="primary" busy={rename.isPending} disabled={!dirty}>
                Save
              </Button>
              {rename.isSuccess && !dirty ? <span className="text-caption text-go">Saved</span> : null}
            </div>
            {rename.isError ? <ErrorText>{errorMessage(rename.error)}</ErrorText> : null}
          </form>
        </section>

        <section className="card p-4 sm:p-5">
          <h2 className="text-h4 text-chalk">Delete account</h2>
          <p className="mt-1 text-body text-chalk-dim">
            Removes your recordings, your share links, and any team that only has you in it, and
            signs out every Mac. If you are the only owner of a team with other people in it, make
            someone else an owner first.
          </p>
          <div className="mt-4 max-w-xs">
            <Input
              label='Type "delete" to confirm'
              value={confirmDelete}
              onChange={(event) => setConfirmDelete(event.target.value)}
              autoComplete="off"
            />
          </div>
          <Button
            variant="danger"
            className="mt-4"
            disabled={!canDelete}
            busy={remove.isPending}
            onClick={() => remove.mutate()}
          >
            Delete my account
          </Button>
          {remove.isError ? (
            <div className="mt-2">
              <ErrorText>
                {remove.error instanceof ApiError && remove.error.status === 409
                  ? remove.error.message
                  : errorMessage(remove.error)}
              </ErrorText>
            </div>
          ) : null}
        </section>
      </div>
    </>
  );
}
